import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/ScrollToTop';
import { useAuthStore } from './store/authStore';
import AppLayout from './components/layout/AppLayout';
import Home from './pages/Home';
import ContactUs from './pages/ContactUs';
import HowToUse from './pages/HowToUse';
import UseCases from './pages/UseCases';
import UseCaseDetail from './pages/UseCaseDetail';
import PrivateAI from './pages/PrivateAI';
import Pricing from './pages/Pricing';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ChatInterface from './components/chat/ChatInterface';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import DataSources from './pages/DataSources';
import { DataSourceFlow } from './connectors/googleDrive';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import GlobalDocuments from './pages/GlobalDocuments';
import KnowledgeBase from './pages/KnowledgeBase';
import RateLimitExceededModal from './components/RateLimitExceededModal';
import { useTheme } from './theme';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to app if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
  allowAuthenticated?: boolean; // Allow authenticated users to access this route
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children, allowAuthenticated = false }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated && !allowAuthenticated) {
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { initializeAuth } = useAuthStore();
  const { theme } = useTheme();

  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <RateLimitExceededModal />
        <Routes>
          {/* Public Routes */}
          {/* Root path - always show home page */}
          <Route
            path="/"
            element={
              <PublicRoute allowAuthenticated={true}>
                <Home />
              </PublicRoute>
            }
          />
          <Route
            path="/home"
            element={
              <PublicRoute allowAuthenticated={true}>
                <Home />
              </PublicRoute>
            }
          />
          <Route
            path="/contact"
            element={
              <PublicRoute allowAuthenticated={true}>
                <ContactUs />
              </PublicRoute>
            }
          />
          <Route
            path="/how-to-use"
            element={
              <PublicRoute allowAuthenticated={true}>
                <HowToUse />
              </PublicRoute>
            }
          />
          <Route
            path="/use-cases"
            element={
              <PublicRoute allowAuthenticated={true}>
                <UseCases />
              </PublicRoute>
            }
          />
          <Route
            path="/use-cases/:useCaseId"
            element={
              <PublicRoute allowAuthenticated={true}>
                <UseCaseDetail />
              </PublicRoute>
            }
          />
          <Route
            path="/private-ai"
            element={
              <PublicRoute allowAuthenticated={true}>
                <PrivateAI />
              </PublicRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <PublicRoute allowAuthenticated={true}>
                <Pricing />
              </PublicRoute>
            }
          />
          <Route
            path="/terms"
            element={
              <PublicRoute allowAuthenticated={true}>
                <TermsAndConditions />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicRoute allowAuthenticated={true}>
                <PrivacyPolicy />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Protected Routes - App Layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/chat" element={<ChatInterface />} />
            <Route path="/chat/:threadId" element={<ChatInterface />} />
            <Route path="/folder/:folderId" element={<ChatInterface />} />
            <Route path="/documents/:folderId" element={<Documents />} />
            <Route path="/document/:docId" element={<DocumentDetail />} />
            <Route path="/data-sources/add" element={<DataSourceFlow />} />
            <Route path="/data-sources/:id" element={<DataSources />} />
            <Route path="/data-sources" element={<DataSources />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/global-documents" element={<GlobalDocuments />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
          </Route>

          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: theme.primary[600],
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

export default App;